import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import LocationImage from '../LocationImage';
import { getString, safeRender, renderRecommendations } from './helpers';
import { Car, Loader2, ExternalLink, ArrowRight, Users, MapPin } from 'lucide-react';

export const TransportTab = ({
  generatedItinerary,
  currencyMode,
  convertedBudget,
  convertingCurrency,
  toggleCurrency,
  getNationalityCurrency,
  detectBudgetCurrency,
  trip_input,
}) => {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <Button
          variant="default"
          size="sm"
          onClick={toggleCurrency}
          disabled={convertingCurrency}
          className="text-xs bg-ocean hover:bg-ocean-light text-white h-8 px-3"
          data-testid="transport-currency-toggle"
        >
          {convertingCurrency ? (
            <Loader2 className="w-3 h-3 animate-spin mr-1" />
          ) : (
            <span className="mr-1">&#8644;</span>
          )}
          {currencyMode === 'destination' ? `Show in ${getNationalityCurrency(trip_input?.nationality)}` : `Show in ${detectBudgetCurrency()}`}
        </Button>
      </div>
      <Card className="bg-white/90 backdrop-blur-sm border-sky/20">
        <CardHeader>
          <CardTitle className="font-serif text-xl text-charcoal flex items-center gap-2">
            <Car className="w-5 h-5 text-ocean" />
            Transportation Guide
          </CardTitle>
        </CardHeader>
        <CardContent>
          {generatedItinerary.transport ? (
            <div className="space-y-4">
              {Array.isArray(generatedItinerary.transport) ? (
                generatedItinerary.transport.map((item, index) => (
                  <div key={item.mode || item.type ? `${item.mode || item.type}-${item.city || index}` : index} className="rounded-xl border border-sky/20 overflow-hidden hover:shadow-md transition-all bg-white/90">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                      <div className="h-48 overflow-hidden bg-gray-100">
                        <LocationImage
                          query={`${getString(item.mode || item.type || 'transport')} ${getString(item.city || trip_input?.destination_city || '')}`}
                          type="transport"
                          index={index}
                          className="w-full h-full object-cover"
                          alt={getString(item.mode || item.type || 'Transport')}
                        />
                      </div>
                      <div className="p-5">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-charcoal text-lg">{getString(item.mode || item.type || item.name || item.title || item)}</h4>
                          {(item.cost_estimate || item.cost || item.estimated_cost) && (
                            <span className="bg-gold/10 text-gold-dark px-3 py-1 rounded-full text-sm font-bold">
                              {currencyMode === 'home' && convertedBudget?.rate
                                ? (() => {
                                    const costStr = getString(item.cost_estimate || item.cost || item.estimated_cost);
                                    // Strip the (approx. ...) parenthetical for home currency
                                    let result = String(costStr).replace(/\s*\(approx\.?[^)]*\)/gi, '');
                                    // Strip all currency indicators first
                                    result = result.replace(/[A-Z]{0,2}[€$£¥₹₫₱₩]\s*/g, '');
                                    result = result.replace(/\b[A-Z]{3}\s+(?=\d)/g, '');
                                    // Convert all numbers using the exchange rate
                                    result = result.replace(/(\d[\d,]*(?:\.\d+)?)/g, (m) => {
                                      const n = parseFloat(m.replace(/,/g, ''));
                                      return isNaN(n) ? m : Math.round(n * convertedBudget.rate).toLocaleString();
                                    });
                                    return (convertedBudget.currency + ' ' + result).replace(/\s+/g, ' ').trim();
                                  })()
                                : getString(item.cost_estimate || item.cost || item.estimated_cost)}
                            </span>
                          )}
                        </div>
                        {(item.description || item.details) && (
                          <p className="text-sm text-charcoal/70">{getString(item.description || item.details)}</p>
                        )}
                        {item.duration && <p className="text-sm text-charcoal/60 mt-1">Duration: {getString(item.duration)}</p>}
                        {item.recommendations && <div className="mt-2">{renderRecommendations(item.recommendations)}</div>}
                      </div>
                    </div>
                  </div>
                ))
              ) : typeof generatedItinerary.transport === 'object' ? (
                <div className="p-4 bg-burnt/5 rounded-lg border border-burnt/10">
                  {Object.entries(generatedItinerary.transport).map(([key, val]) => (
                    <div key={key} className="mb-2">
                      <span className="font-medium capitalize">{key.replace(/_/g, ' ')}: </span>
                      <span>{safeRender(val)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-charcoal">{getString(generatedItinerary.transport)}</p>
              )}
            </div>
          ) : (
            <p className="text-charcoal/60">No transport information available</p>
          )}
        </CardContent>
      </Card>

      {/* Transfer Services */}
      <h3 className="font-serif text-lg text-white drop-shadow-md flex items-center gap-2 mt-2">
        <Users className="w-5 h-5 text-green-400" /> Airport & City Transfers
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="transport-services">
        <a href="https://tpm.lv/sD3CoTaI" target="_blank" rel="noopener noreferrer"
          className="group bg-white/95 backdrop-blur-sm rounded-xl border border-green-500/20 p-5 hover:shadow-lg transition-all hover:border-green-500/40">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h4 className="text-charcoal font-semibold text-sm mb-1">Welcome Pickups</h4>
              <p className="text-charcoal/60 text-xs mb-2">Professional drivers waiting at the airport for a stress-free arrival.</p>
              <span className="inline-flex items-center gap-1 text-green-600 font-semibold text-xs group-hover:text-green-700">
                Book Now <ExternalLink className="w-3 h-3" />
              </span>
            </div>
          </div>
        </a>

        <a href="https://gettransfer.tpm.lv/CDo528tq" target="_blank" rel="noopener noreferrer"
          className="group bg-white/95 backdrop-blur-sm rounded-xl border border-ocean/20 p-5 hover:shadow-lg transition-all hover:border-ocean/40">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-ocean to-sky flex items-center justify-center flex-shrink-0">
              <ArrowRight className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h4 className="text-charcoal font-semibold text-sm mb-1">GetTransfer</h4>
              <p className="text-charcoal/60 text-xs mb-2">Door-to-door transfers between cities, airports, and hotels.</p>
              <span className="inline-flex items-center gap-1 text-ocean font-semibold text-xs group-hover:text-sky">
                Compare & Book <ExternalLink className="w-3 h-3" />
              </span>
            </div>
          </div>
        </a>

        <a href="https://kiwitaxi.tpm.lv/oSXEVK28" target="_blank" rel="noopener noreferrer"
          className="group bg-white/95 backdrop-blur-sm rounded-xl border border-lime-500/20 p-5 hover:shadow-lg transition-all hover:border-lime-500/40">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-lime-500 to-lime-600 flex items-center justify-center flex-shrink-0">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h4 className="text-charcoal font-semibold text-sm mb-1">Kiwitaxi</h4>
              <p className="text-charcoal/60 text-xs mb-2">Fixed-price airport transfers and intercity rides worldwide.</p>
              <span className="inline-flex items-center gap-1 text-lime-600 font-semibold text-xs group-hover:text-lime-700">
                Book Transfer <ExternalLink className="w-3 h-3" />
              </span>
            </div>
          </div>
        </a>

        <a href="https://intui.tpm.lv/lfzcVO2u" target="_blank" rel="noopener noreferrer"
          className="group bg-white/95 backdrop-blur-sm rounded-xl border border-indigo-500/20 p-5 hover:shadow-lg transition-all hover:border-indigo-500/40">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h4 className="text-charcoal font-semibold text-sm mb-1">Intui Travel</h4>
              <p className="text-charcoal/60 text-xs mb-2">Airport shuttle and private transfers in 150+ countries.</p>
              <span className="inline-flex items-center gap-1 text-indigo-600 font-semibold text-xs group-hover:text-indigo-700">
                Book Shuttle <ExternalLink className="w-3 h-3" />
              </span>
            </div>
          </div>
        </a>
      </div>

      {/* Car Rentals */}
      <h3 className="font-serif text-lg text-white drop-shadow-md flex items-center gap-2 mt-2">
        <Car className="w-5 h-5 text-blue-400" /> Car Rentals
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="car-rental-services">
        <a href="https://discovercars.tpm.lv/bpojtima" target="_blank" rel="noopener noreferrer"
          className="group bg-white/95 backdrop-blur-sm rounded-xl border border-blue-500/20 p-5 hover:shadow-lg transition-all hover:border-blue-500/40">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0">
              <Car className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h4 className="text-charcoal font-semibold text-sm mb-1">Discovercars</h4>
              <p className="text-charcoal/60 text-xs mb-2">Compare car rental deals from top providers. Best price guaranteed.</p>
              <span className="inline-flex items-center gap-1 text-blue-600 font-semibold text-xs group-hover:text-blue-700">
                Search Cars <ExternalLink className="w-3 h-3" />
              </span>
            </div>
          </div>
        </a>

        <a href="https://localrent.tpm.lv/qq1aKn1w" target="_blank" rel="noopener noreferrer"
          className="group bg-white/95 backdrop-blur-sm rounded-xl border border-teal-500/20 p-5 hover:shadow-lg transition-all hover:border-teal-500/40">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center flex-shrink-0">
              <Car className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h4 className="text-charcoal font-semibold text-sm mb-1">Localrent</h4>
              <p className="text-charcoal/60 text-xs mb-2">Rent from local owners — no hidden fees, no deposits required.</p>
              <span className="inline-flex items-center gap-1 text-teal-600 font-semibold text-xs group-hover:text-teal-700">
                Browse Rentals <ExternalLink className="w-3 h-3" />
              </span>
            </div>
          </div>
        </a>

        <a href="https://economybookings.tpm.lv/ZTRwuuJf" target="_blank" rel="noopener noreferrer"
          className="group bg-white/95 backdrop-blur-sm rounded-xl border border-emerald-500/20 p-5 hover:shadow-lg transition-all hover:border-emerald-500/40">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center flex-shrink-0">
              <Car className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h4 className="text-charcoal font-semibold text-sm mb-1">Economybookings</h4>
              <p className="text-charcoal/60 text-xs mb-2">Affordable car hire in 30,000+ locations worldwide.</p>
              <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold text-xs group-hover:text-emerald-700">
                Find Deals <ExternalLink className="w-3 h-3" />
              </span>
            </div>
          </div>
        </a>

        <a href="https://getrentacar.tpm.lv/k2fzyJ8h" target="_blank" rel="noopener noreferrer"
          className="group bg-white/95 backdrop-blur-sm rounded-xl border border-cyan-500/20 p-5 hover:shadow-lg transition-all hover:border-cyan-500/40">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center flex-shrink-0">
              <Car className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h4 className="text-charcoal font-semibold text-sm mb-1">GetRentacar</h4>
              <p className="text-charcoal/60 text-xs mb-2">Local car rental deals — no credit card required at many locations.</p>
              <span className="inline-flex items-center gap-1 text-cyan-600 font-semibold text-xs group-hover:text-cyan-700">
                Rent a Car <ExternalLink className="w-3 h-3" />
              </span>
            </div>
          </div>
        </a>
      </div>
    </div>
  );
};
